import { useState } from "react";
import {
  BookOpen,
  Paperclip,
  ScrollText,
  Link as LinkIcon,
  X,
} from "lucide-react";

const SNIPPET_LENGTH = 180;

/** Иконка Telegram (бумажный самолётик), бренд. */
function TelegramIcon({ size = 18, className = "" }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  );
}

/** Иконка GitHub (марка). */
function GitHubIcon({ size = 18, className = "" }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
    </svg>
  );
}

/** Иконка ClickHouse (домик со стрелкой, упрощённый логотип). */
function ClickHouseIcon({ size = 18, className = "" }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
      <path d="M19 13l-4 4m0 0l-4-4m4 4V9" />
    </svg>
  );
}

export type SourceItem = {
  content: string;
  metadata?: Record<string, any>;
  score: number;
};

const TELEGRAM_PREFIX = "https://t.me/";

// Не используем хардкод канала как fallback — если канал не известен, URL недоступен
const DEFAULT_TELEGRAM_CHANNEL = "";

/**
 * Нормализует Telegram-ссылку к виду https://t.me/CHANNEL/MESSAGE_ID.
 * Поддерживает: t.me/chan/123, telegram.me/..., telegram//chat#msg333712 и т.п.
 */
function buildTelegramUrl(url: string, meta: Record<string, any>): string {
  const raw = (url || "").trim();

  // Формат telegram//chat#msg333712 или https://telegram.org/.../#msg123 — достаём ID из хеша
  const hashMatch = raw.match(/#(?:msg[-_]?)?(\d+)/i);
  const idFromHash = hashMatch ? hashMatch[1] : null;

  const rest = raw
    .replace(/^https?:\/\//i, "")
    .replace(/#.*$/, "")
    .replace(/\?.*$/, "")
    .replace(/^telegram\.(me|dog)\/+?/i, "t.me/")
    .replace(/^t\.me\/+?/i, "")
    .replace(/\/+$/, "");

  // Только хеш с msg-id без нормального пути → канал из мета или clickhouse_ru
  if (idFromHash && (!rest || /^telegram\/\/?/i.test(rest) || rest === "chat")) {
    const ch =
      (meta?.telegram_channel ?? meta?.channel ?? meta?.username ?? DEFAULT_TELEGRAM_CHANNEL)
        .toString()
        .replace(/^@/, "")
        .trim();
    if (ch) return TELEGRAM_PREFIX + ch + "/" + idFromHash;
  }

  if (!rest && (meta?.telegram_channel || meta?.channel || meta?.username)) {
    const ch = (meta.telegram_channel || meta.channel || meta.username || "").replace(/^@/, "");
    const id = String(meta.telegram_message_id ?? meta.message_id ?? meta.msg_id ?? meta.content_id ?? "").trim();
    if (ch && id && /^\d+$/.test(id)) return TELEGRAM_PREFIX + ch + "/" + id;
    if (ch) return TELEGRAM_PREFIX + ch;
    return "";
  }

  const path = rest.replace(/^telegram\/+/, "");
  const parts = path.split("/").filter(Boolean);
  const channelPart = parts[0]?.replace(/^@/, "") || "";
  const numericId = parts.find((p) => /^\d+$/.test(p)) ?? idFromHash;
  const channel = channelPart && !/^\d+$/.test(channelPart) ? channelPart : "";

  if (channel && numericId) return TELEGRAM_PREFIX + channel + "/" + numericId;
  if (channel) return TELEGRAM_PREFIX + channel;
  if (idFromHash) {
    const ch =
      (meta?.telegram_channel ?? meta?.channel ?? meta?.username ?? DEFAULT_TELEGRAM_CHANNEL)
        .toString()
        .replace(/^@/, "")
        .trim();
    if (ch) return TELEGRAM_PREFIX + ch + "/" + idFromHash;
  }
  if (path && /^\d+$/.test(path)) return TELEGRAM_PREFIX + "c/" + path;

  const fallbackId = raw.match(/(\d{5,})/);
  if (channel && fallbackId) return TELEGRAM_PREFIX + channel + "/" + fallbackId[1];
  if (fallbackId && (meta?.telegram_channel ?? meta?.channel ?? meta?.username)) {
    const ch = (meta.telegram_channel ?? meta.channel ?? meta.username ?? DEFAULT_TELEGRAM_CHANNEL).toString().replace(/^@/, "");
    return TELEGRAM_PREFIX + ch + "/" + fallbackId[1];
  }

  return path ? TELEGRAM_PREFIX + path : "";
}

function normalizeUrl(
  url: string,
  meta: Record<string, any>
): string {
  if (!url && !meta) return "";
  const u = (url || "").trim();
  const lower = u.toLowerCase();
  const isTelegram =
    lower.includes("t.me") ||
    lower.includes("telegram.me") ||
    lower.includes("telegram//") ||
    lower.startsWith("telegram/") ||
    lower.includes("telegram.org") ||
    (meta?.source_type || meta?.content_type || meta?.source || "").toString().toLowerCase() === "telegram";

  if (isTelegram) {
    const built = buildTelegramUrl(u || "", meta || {});
    if (built) return built;
  }

  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.includes("t.me") || u.startsWith("t.me/")) return buildTelegramUrl(u, meta || {});
  return "https://" + u.replace(/^\/+/, "");
}

/** Группировка по полю source (или source_type, content_type). */
function getSourceKey(source: SourceItem): string {
  const meta = source.metadata || {};
  const s =
    meta.source ??
    meta.source_type ??
    meta.content_type ??
    "";
  const key = String(s).trim().toLowerCase();
  if (key) return key;
  const url = (meta.url || "").toLowerCase();
  if (url.includes("t.me")) return "telegram";
  if (url.includes("github.com") || url.includes("github.io")) return "github";
  if (url.includes("/docs") || url.includes("clickhouse.com/docs")) return "docs";
  return "other";
}

type IconComponent = React.ComponentType<{ size?: number; className?: string }>;

const SOURCE_ICONS: Record<string, IconComponent> = {
  telegram: TelegramIcon,
  telegram_faq: TelegramIcon,
  docs: ClickHouseIcon,
  documentation: ClickHouseIcon,
  github: GitHubIcon,
  "user story": ScrollText,
  user_story: ScrollText,
  blog: ScrollText,
  story: ScrollText,
  forum: ScrollText,
  altinity_kb: BookOpen,
  stackoverflow_qa: BookOpen,
};

function getIconForKey(key: string): IconComponent {
  return SOURCE_ICONS[key] ?? LinkIcon;
}

/** Человекочитаемая подпись группы по ключу. */
function getGroupLabel(key: string): string {
  const labels: Record<string, string> = {
    telegram: "Telegram",
    telegram_faq: "Telegram",
    docs: "Документация",
    documentation: "Документация",
    github: "GitHub",
    "user story": "User story",
    user_story: "User story",
    blog: "Блог",
    story: "User story",
    forum: "Форум",
    altinity_kb: "Altinity KB",
    stackoverflow_qa: "Stack Overflow",
  };
  return labels[key] ?? key;
}

function groupBySource(sources: SourceItem[]): Map<string, SourceItem[]> {
  const map = new Map<string, SourceItem[]>();
  const order = ["docs", "documentation", "telegram", "user_story", "user story", "github", "blog", "story", "forum", "other"];
  for (const s of sources) {
    const key = getSourceKey(s);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(s);
  }
  return map;
}

function sortGroupKeys(keys: string[]): string[] {
  const order = ["docs", "documentation", "telegram", "user_story", "user story", "github", "blog", "story", "forum", "other"];
  return [...keys].sort((a, b) => {
    const i = order.indexOf(a);
    const j = order.indexOf(b);
    if (i !== -1 && j !== -1) return i - j;
    if (i !== -1) return -1;
    if (j !== -1) return 1;
    return a.localeCompare(b);
  });
}

/** Уникальные источники по (url, title), порядок: docs → telegram → … */
function getUniqueSourcesForList(sources: SourceItem[]): SourceItem[] {
  const grouped = groupBySource(sources);
  const keys = sortGroupKeys(Array.from(grouped.keys()));
  const seen = new Set<string>();
  const out: SourceItem[] = [];
  for (const k of keys) {
    for (const item of grouped.get(k)!) {
      const url = normalizeUrl(item.metadata?.url || "", item.metadata || {});
      const title = item.metadata?.title || item.metadata?.course_slug || "";
      if (seen.has(url + "\0" + title)) continue;
      seen.add(url + "\0" + title);
      out.push(item);
    }
  }
  return out;
}

/** Цветной dot-индикатор релевантности RAG-источника */
function ScoreDot({ score }: { score: number }) {
  if (typeof score !== "number" || score <= 0) return null;
  const color =
    score >= 0.7 ? "bg-green-500" :
    score >= 0.4 ? "bg-yellow-400" :
    "bg-neutral-400";
  const pct = Math.round(score * 100);
  return (
    <span
      className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 mt-1 ${color}`}
      title={`Релевантность: ${pct}%`}
      aria-label={`Релевантность ${pct}%`}
    />
  );
}

function truncateSnippet(text: string, maxLen: number): string {
  if (!text || !text.trim()) return "";
  const t = text.trim().replace(/\s+/g, " ");
  return t.length <= maxLen ? t : t.slice(0, maxLen).trim() + "…";
}

interface AITutorSourcesProps {
  sources: SourceItem[];
  className?: string;
  borderClass?: string;
  showEmptyMessage?: boolean;
  popupTitle?: string;
  /** default: кнопка с числом и текстом; compact: иконка + число; iconOnly: только скрепка в зелёном квадрате; inline-list: список заголовков под сообщением */
  variant?: "default" | "compact" | "iconOnly" | "inline-list";
}

const INLINE_LIST_MAX = 5;

export function AITutorSources({
  sources,
  className = "",
  borderClass = "border-primary/20",
  showEmptyMessage = true,
  popupTitle,
  variant = "default",
}: AITutorSourcesProps) {
  const [popupOpen, setPopupOpen] = useState(false);

  if (!sources || sources.length === 0) {
    if (!showEmptyMessage) return null;
    return (
      <div className={`mt-3 pt-3 border-t ${borderClass} ${className}`}>
        <p className="text-xs opacity-50 italic">Источники не найдены</p>
      </div>
    );
  }

  // После LLM Judge на бэкенде список sources уже отфильтрован.
  // Для счётчика показываем именно количество таких источников,
  // а в списке ниже группируем и убираем дубликаты по (url, title),
  // чтобы не засорять UI множеством чанков с одной и той же страницы.
  const uniqueList = getUniqueSourcesForList(sources);

  // Вариант inline-list: компактный список заголовков под сообщением
  if (variant === "inline-list") {
    const visible = uniqueList.slice(0, INLINE_LIST_MAX);
    const extra = uniqueList.length - visible.length;
    return (
      <>
        <div className={`flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-xs text-neutral-text/60 ${className}`}>
          <span className="shrink-0 font-medium text-neutral-text/50">Источники:</span>
          {visible.map((s, idx) => {
            const meta = s.metadata || {};
            const title = (meta.title || meta.course_slug || meta.source || "Источник") as string;
            const url = normalizeUrl((meta.url || "") as string, meta);
            const label = title.length > 55 ? title.slice(0, 55) + "…" : title;
            return (
              <span key={idx} className="inline-flex items-center gap-1">
                {idx > 0 && <span className="text-neutral-text/30">·</span>}
                <ScoreDot score={s.score} />
                {url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary/70 hover:text-primary hover:underline transition-colors"
                    title={title}
                  >
                    {label}
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={() => setPopupOpen(true)}
                    className="text-primary/70 hover:text-primary hover:underline transition-colors text-left"
                    title={title}
                  >
                    {label}
                  </button>
                )}
              </span>
            );
          })}
          {extra > 0 && (
            <span className="inline-flex items-center gap-1">
              <span className="text-neutral-text/30">·</span>
              <button
                type="button"
                onClick={() => setPopupOpen(true)}
                className="text-primary/70 hover:text-primary hover:underline transition-colors"
              >
                +{extra} ещё
              </button>
            </span>
          )}
        </div>
        {popupOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={() => setPopupOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="sources-popup-title-il"
          >
            <div
              className="bg-neutral-light dark:bg-neutral-dark rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between shrink-0 p-4 border-b border-neutral-border/30">
                <h2 id="sources-popup-title-il" className="text-base font-semibold text-neutral-text">
                  {popupTitle ? `Источники: ${popupTitle}` : "Источники"}
                </h2>
                <button
                  type="button"
                  onClick={() => setPopupOpen(false)}
                  className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-neutral-border/20 text-neutral-text focus:outline-none focus:ring-2 focus:ring-primary/30"
                  aria-label="Закрыть"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="overflow-y-auto flex-1 p-4 space-y-6">
                {(() => {
                  const groupedByType = new Map<string, SourceItem[]>();
                  for (const s of uniqueList) {
                    const k = getSourceKey(s);
                    if (!groupedByType.has(k)) groupedByType.set(k, []);
                    groupedByType.get(k)!.push(s);
                  }
                  const keys = sortGroupKeys(Array.from(groupedByType.keys()));
                  return keys.map((key) => {
                    const items = groupedByType.get(key) || [];
                    const Icon = getIconForKey(key);
                    const label = getGroupLabel(key);
                    return (
                      <section key={key} className="space-y-2">
                        <div className="flex items-center gap-2 pb-1 border-b border-neutral-border/30">
                          <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary">
                            <Icon size={18} />
                          </span>
                          <h3 className="text-sm font-semibold text-neutral-text">{label}</h3>
                          <span className="text-xs text-neutral-text/60">({items.length})</span>
                        </div>
                        <ul className="space-y-2 list-none m-0 p-0">
                          {items.map((source, idx) => {
                            const meta = source.metadata || {};
                            const title = (meta.title || meta.course_slug || "Источник") as string;
                            const url = normalizeUrl((meta.url || "") as string, meta);
                            const snippet = truncateSnippet(source.content || "", SNIPPET_LENGTH);
                            return (
                              <li key={idx} className="flex gap-3 p-3 rounded-lg bg-neutral-border/10 hover:bg-neutral-border/20 transition-colors">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-start gap-1.5">
                                    <ScoreDot score={source.score} />
                                    {url ? (
                                      <a href={url} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline block truncate" title={title}>
                                        {title.length > 80 ? title.slice(0, 80) + "…" : title}
                                      </a>
                                    ) : (
                                      <span className="font-medium text-neutral-text block truncate" title={title}>
                                        {title.length > 80 ? title.slice(0, 80) + "…" : title}
                                      </span>
                                    )}
                                  </div>
                                  {snippet && <p className="text-xs text-neutral-text/80 mt-1 line-clamp-3">{snippet}</p>}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </section>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
  const count = sources.length;
  const groupedByType = (() => {
    const map = new Map<string, SourceItem[]>();
    for (const s of uniqueList) {
      const k = getSourceKey(s);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(s);
    }
    return map;
  })();
  const groupKeys = sortGroupKeys(Array.from(groupedByType.keys()));

  const buttonContent =
    variant === "iconOnly" ? (
      <button
        type="button"
        onClick={() => setPopupOpen(true)}
        className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 hover:bg-primary/20 border border-primary/20 text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30"
        aria-label={`Показать ${count} источников`}
      >
        <Paperclip size={18} />
      </button>
    ) : (
      <button
        type="button"
        onClick={() => setPopupOpen(true)}
        className={
          variant === "compact"
            ? "inline-flex items-center gap-1.5 py-1.5 px-2 rounded-md text-neutral-text/70 hover:text-neutral-text hover:bg-neutral-border/20 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30"
            : "inline-flex items-center gap-2 min-h-[44px] py-2 px-3 rounded-lg bg-primary/10 hover:bg-primary/20 border border-primary/20 text-primary text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30"
        }
        aria-label={`Показать ${count} источников`}
      >
        <LinkIcon size={variant === "compact" ? 14 : 16} />
        <span>{count}</span>
        {variant === "default" && (
          <span>{count === 1 ? "источник" : count < 5 ? "источника" : "источников"}</span>
        )}
      </button>
    );

  return (
    <>
      <div
        className={
          variant === "iconOnly"
            ? `shrink-0 ${className}`
            : variant === "compact"
              ? `shrink-0 ${className}`
              : `mt-3 pt-3 border-t ${borderClass} ${className}`
        }
      >
        {buttonContent}
      </div>

      {popupOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setPopupOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="sources-popup-title"
        >
          <div
            className="bg-neutral-light dark:bg-neutral-dark rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between shrink-0 p-4 border-b border-neutral-border/30">
              <h2 id="sources-popup-title" className="text-base font-semibold text-neutral-text">
                {popupTitle ? `Источники: ${popupTitle}` : "Источники"}
              </h2>
              <button
                type="button"
                onClick={() => setPopupOpen(false)}
                className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-neutral-border/20 text-neutral-text focus:outline-none focus:ring-2 focus:ring-primary/30"
                aria-label="Закрыть"
              >
                <X size={20} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-4 space-y-6">
              {groupKeys.map((key) => {
                const items = groupedByType.get(key) || [];
                const Icon = getIconForKey(key);
                const label = getGroupLabel(key);
                return (
                  <section key={key} className="space-y-2">
                    <div className="flex items-center gap-2 pb-1 border-b border-neutral-border/30">
                      <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary">
                        <Icon size={18} />
                      </span>
                      <h3 className="text-sm font-semibold text-neutral-text">{label}</h3>
                      <span className="text-xs text-neutral-text/60">({items.length})</span>
                    </div>
                    <ul className="space-y-2 list-none m-0 p-0">
                      {items.map((source, idx) => {
                        const meta = source.metadata || {};
                        const title = meta.title || meta.course_slug || "Источник";
                        const url = normalizeUrl(meta.url || "", meta);
                        const snippet = truncateSnippet(source.content || "", SNIPPET_LENGTH);
                        return (
                          <li
                            key={idx}
                            className="flex gap-3 p-3 rounded-lg bg-neutral-border/10 hover:bg-neutral-border/20 transition-colors"
                          >
                            <div className="min-w-0 flex-1">
                              {url ? (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-medium text-primary hover:underline block truncate"
                                  title={title}
                                >
                                  {title.length > 80 ? title.slice(0, 80) + "…" : title}
                                </a>
                              ) : (
                                <span className="font-medium text-neutral-text block truncate" title={title}>
                                  {title.length > 80 ? title.slice(0, 80) + "…" : title}
                                </span>
                              )}
                              {snippet && (
                                <p className="text-xs text-neutral-text/80 mt-1 line-clamp-3">{snippet}</p>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
